// src/components/Store/Checkout.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import PublicLayout from '../public/PublicLayout';
import axios from 'axios';

// Initialize Stripe (will be loaded from backend)
let stripePromise = null;

// ARCA Color Scheme
const ARCA_COLORS = {
  primary: '#6B8E23',
  secondary: '#2C5234',
  success: '#28a745',
  danger: '#dc3545',
  warning: '#ffc107',
  info: '#17a2b8',
  light: '#f8f9fa',
  dark: '#343a40'
};

const CheckoutForm = ({ user, onLogout }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [stripePublicKey, setStripePublicKey] = useState('');
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: user?.email || '',
    phone: '',
    shipping: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      address1: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US'
    },
    shippingMethod: 'standard',
    payment: {
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      nameOnCard: '',
      paymentMethod: 'card'
    }
  });

  const [errors, setErrors] = useState({});

  // Load Stripe publishable key
  useEffect(() => {
    const loadStripeKey = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/publishable-key`);
        const data = await response.json();
        if (data.success && data.publishableKey) {
          setStripePublicKey(data.publishableKey);
          stripePromise = loadStripe(data.publishableKey);
        }
      } catch (error) {
        console.error('Error loading Stripe key:', error);
      }
    };
    loadStripeKey();
  }, []);

  useEffect(() => {
    const savedCart = localStorage.getItem('arcaCart');
    if (savedCart) {
      const cartData = JSON.parse(savedCart);
      setCart(cartData);
      if (cartData.length === 0) navigate('/cart');
    } else {
      navigate('/cart');
    }
  }, []);

  const handleInputChange = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const calculateSubtotal = () => cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  const calculateShipping = () => {
    const subtotal = calculateSubtotal();
    if (formData.shippingMethod === 'express') return 24.99;
    if (formData.shippingMethod === 'overnight') return 39.99;
    return subtotal >= 100 ? 0 : 15.99;
  };
  const calculateTax = () => calculateSubtotal() * 0.08;
  const calculateTotal = () => calculateSubtotal() + calculateShipping() + calculateTax();

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    }
    if (!formData.shipping.firstName) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.shipping.lastName) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.shipping.address1) {
      newErrors.address1 = 'Address is required';
    }
    if (!formData.shipping.city) {
      newErrors.city = 'City is required';
    }
    if (!formData.shipping.state) {
      newErrors.state = 'State is required';
    }
    if (!formData.shipping.postalCode) {
      newErrors.postalCode = 'ZIP code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const placeOrder = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!stripe || !elements) {
      toast.error('Stripe is not loaded. Please refresh the page.');
      return;
    }

    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');

      // Prepare headers - only include Authorization if token exists
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Step 1: Create the order on the backend using direct endpoint
      const orderResponse = await axios.post(
        `${API_BASE}/api/store/orders/direct`,
        {
          customer_email: formData.email,
          customer_name: `${formData.shipping.firstName} ${formData.shipping.lastName}`,
          customer_phone: formData.phone,
          billing_address: {
            first_name: formData.shipping.firstName,
            last_name: formData.shipping.lastName,
            address_1: formData.shipping.address1,
            city: formData.shipping.city,
            state: formData.shipping.state,
            postal_code: formData.shipping.postalCode,
            country: formData.shipping.country
          },
          shipping_address: {
            first_name: formData.shipping.firstName,
            last_name: formData.shipping.lastName,
            address_1: formData.shipping.address1,
            city: formData.shipping.city,
            state: formData.shipping.state,
            postal_code: formData.shipping.postalCode,
            country: formData.shipping.country
          },
          payment_method: 'stripe_card',
          items: cart.map(item => ({
            product_id: item.id,
            quantity: item.quantity,
            price: item.price,
            name: item.name
          })),
          subtotal: calculateSubtotal(),
          shipping_cost: calculateShipping(),
          tax: calculateTax(),
          total: calculateTotal()
        },
        { headers }
      );

      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.message || 'Failed to create order');
      }

      const orderId = orderResponse.data.data.order.id;
      console.log('Order created:', orderId);

      // Step 2: Create Stripe Payment Intent
      const paymentIntentResponse = await axios.post(
        `${API_BASE}/stripe/create-store-payment-intent`,
        {
          amount: calculateTotal(),
          items: cart.map(item => ({
            product_id: item.id,
            quantity: item.quantity,
            price: item.price,
            name: item.name
          })),
          userId: user?.id || 'guest',
          email: formData.email
        },
        { headers }
      );

      if (!paymentIntentResponse.data.success) {
        throw new Error(paymentIntentResponse.data.message || 'Failed to create payment intent');
      }

      const { clientSecret } = paymentIntentResponse.data;
      console.log('Payment intent created');

      // Step 3: Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: `${formData.shipping.firstName} ${formData.shipping.lastName}`,
            email: formData.email,
            phone: formData.phone,
            address: {
              line1: formData.shipping.address1,
              city: formData.shipping.city,
              state: formData.shipping.state,
              postal_code: formData.shipping.postalCode,
              country: formData.shipping.country
            }
          }
        }
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent.status === 'succeeded') {
        // Payment successful!
        localStorage.removeItem('arcaCart');
        toast.success('Order placed successfully!');
        navigate('/order-success', {
          state: {
            orderNumber: orderId,
            total: calculateTotal().toFixed(2)
          }
        });
      } else {
        throw new Error('Payment was not successful');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error(error.message || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const removeFromCart = (productId) => {
    const newCart = cart.filter(item => item.id !== productId);
    setCart(newCart);
    localStorage.setItem('arcaCart', JSON.stringify(newCart));
    toast.success('Product removed from cart');

    // If cart is empty, redirect to store
    if (newCart.length === 0) {
      navigate('/store');
    }
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }

    const newCart = cart.map(item =>
      item.id === productId ? { ...item, quantity: newQuantity } : item
    );
    setCart(newCart);
    localStorage.setItem('arcaCart', JSON.stringify(newCart));
  };

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  return (
    <PublicLayout>
      <div style={{ backgroundColor: ARCA_COLORS.light, minHeight: '100vh' }} className="py-5">
        <div className="container">
          {/* Page Header */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex align-items-center mb-3">
                <Link to="/store" className="btn btn-outline-secondary btn-sm me-3">
                  <i className="bi bi-arrow-left me-2"></i>Back to Store
                </Link>
              </div>
              <h1 className="display-5 fw-bold mb-2" style={{ color: ARCA_COLORS.secondary }}>
                <i className="bi bi-cart-check me-3"></i>Checkout
              </h1>
              <p className="text-muted">Complete your purchase securely</p>
            </div>
          </div>

          <div className="row g-4">
            {/* Checkout Form */}
            <div className="col-lg-7">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header" style={{ backgroundColor: ARCA_COLORS.primary, color: 'white' }}>
                  <h5 className="mb-0">
                    <i className="bi bi-person-circle me-2"></i>Contact Information
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Email Address *</label>
                      <input
                        type="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange(null, 'email', e.target.value)}
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        required
                      />
                      {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Phone Number *</label>
                      <input
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => handleInputChange(null, 'phone', e.target.value)}
                        className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                        required
                      />
                      {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header" style={{ backgroundColor: ARCA_COLORS.primary, color: 'white' }}>
                  <h5 className="mb-0">
                    <i className="bi bi-geo-alt-fill me-2"></i>Shipping Address
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">First Name *</label>
                      <input
                        type="text"
                        placeholder="John"
                        value={formData.shipping.firstName}
                        onChange={(e) => handleInputChange('shipping', 'firstName', e.target.value)}
                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                        required
                      />
                      {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Last Name *</label>
                      <input
                        type="text"
                        placeholder="Doe"
                        value={formData.shipping.lastName}
                        onChange={(e) => handleInputChange('shipping', 'lastName', e.target.value)}
                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                        required
                      />
                      {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-bold">Street Address *</label>
                      <input
                        type="text"
                        placeholder="123 Main Street"
                        value={formData.shipping.address1}
                        onChange={(e) => handleInputChange('shipping', 'address1', e.target.value)}
                        className={`form-control ${errors.address1 ? 'is-invalid' : ''}`}
                        required
                      />
                      {errors.address1 && <div className="invalid-feedback">{errors.address1}</div>}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">City *</label>
                      <input
                        type="text"
                        placeholder="New York"
                        value={formData.shipping.city}
                        onChange={(e) => handleInputChange('shipping', 'city', e.target.value)}
                        className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                        required
                      />
                      {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">State *</label>
                      <input
                        type="text"
                        placeholder="NY"
                        value={formData.shipping.state}
                        onChange={(e) => handleInputChange('shipping', 'state', e.target.value)}
                        className={`form-control ${errors.state ? 'is-invalid' : ''}`}
                        required
                      />
                      {errors.state && <div className="invalid-feedback">{errors.state}</div>}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">ZIP Code *</label>
                      <input
                        type="text"
                        placeholder="10001"
                        value={formData.shipping.postalCode}
                        onChange={(e) => handleInputChange('shipping', 'postalCode', e.target.value)}
                        className={`form-control ${errors.postalCode ? 'is-invalid' : ''}`}
                        required
                      />
                      {errors.postalCode && <div className="invalid-feedback">{errors.postalCode}</div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Method */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header" style={{ backgroundColor: ARCA_COLORS.primary, color: 'white' }}>
                  <h5 className="mb-0">
                    <i className="bi bi-truck me-2"></i>Shipping Method
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="d-grid gap-2">
                    <div className="form-check p-3 border rounded" style={{ cursor: 'pointer' }}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="shippingMethod"
                        id="standard"
                        value="standard"
                        checked={formData.shippingMethod === 'standard'}
                        onChange={(e) => handleInputChange(null, 'shippingMethod', e.target.value)}
                      />
                      <label className="form-check-label w-100" htmlFor="standard" style={{ cursor: 'pointer' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>Standard Shipping</strong>
                            <p className="text-muted small mb-0">5-7 business days</p>
                          </div>
                          <strong>{calculateSubtotal() >= 100 ? 'FREE' : '$15.99'}</strong>
                        </div>
                      </label>
                    </div>
                    <div className="form-check p-3 border rounded" style={{ cursor: 'pointer' }}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="shippingMethod"
                        id="express"
                        value="express"
                        checked={formData.shippingMethod === 'express'}
                        onChange={(e) => handleInputChange(null, 'shippingMethod', e.target.value)}
                      />
                      <label className="form-check-label w-100" htmlFor="express" style={{ cursor: 'pointer' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>Express Shipping</strong>
                            <p className="text-muted small mb-0">2-3 business days</p>
                          </div>
                          <strong>$24.99</strong>
                        </div>
                      </label>
                    </div>
                    <div className="form-check p-3 border rounded" style={{ cursor: 'pointer' }}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="shippingMethod"
                        id="overnight"
                        value="overnight"
                        checked={formData.shippingMethod === 'overnight'}
                        onChange={(e) => handleInputChange(null, 'shippingMethod', e.target.value)}
                      />
                      <label className="form-check-label w-100" htmlFor="overnight" style={{ cursor: 'pointer' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>Overnight Shipping</strong>
                            <p className="text-muted small mb-0">Next business day</p>
                          </div>
                          <strong>$39.99</strong>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method - Stripe Card Element */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header" style={{ backgroundColor: ARCA_COLORS.primary, color: 'white' }}>
                  <h5 className="mb-0">
                    <i className="bi bi-credit-card me-2"></i>Payment Information
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="mb-3">
                    <label className="form-label fw-bold">Card Details *</label>
                    <div className="p-3 border rounded" style={{ backgroundColor: '#fff' }}>
                      <CardElement
                        options={{
                          style: {
                            base: {
                              fontSize: '16px',
                              color: '#424770',
                              '::placeholder': {
                                color: '#aab7c4',
                              },
                            },
                            invalid: {
                              color: '#9e2146',
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                  <div className="alert alert-info border-0 mb-0" style={{ backgroundColor: '#e7f3ff' }}>
                    <small>
                      <i className="bi bi-shield-lock-fill me-2" style={{ color: ARCA_COLORS.primary }}></i>
                      Your payment information is securely processed by Stripe. We never store your card details.
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="col-lg-5">
              <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
                <div className="card-header" style={{ backgroundColor: ARCA_COLORS.secondary, color: 'white' }}>
                  <h5 className="mb-0">
                    <i className="bi bi-receipt me-2"></i>Order Summary
                  </h5>
                </div>
                <div className="card-body p-4">
                  {/* Cart Items */}
                  <div className="mb-4">
                    {cart.map(item => (
                      <div key={item.id} className="d-flex align-items-start mb-3 pb-3 border-bottom position-relative">
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="btn btn-sm btn-link position-absolute top-0 end-0 p-0"
                          style={{ color: ARCA_COLORS.danger }}
                          title="Remove item"
                        >
                          <i className="bi bi-x-circle-fill"></i>
                        </button>
                        <div className="me-3">
                          {item.main_image ? (
                            <img
                              src={item.main_image.startsWith('http') ? item.main_image : `${API_BASE}${item.main_image}`}
                              alt={item.name}
                              className="rounded"
                              style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div className="bg-light rounded d-flex align-items-center justify-content-center"
                              style={{ width: '60px', height: '60px' }}>
                              <i className="bi bi-image text-muted"></i>
                            </div>
                          )}
                        </div>
                        <div className="flex-grow-1" style={{ paddingRight: '25px' }}>
                          <h6 className="mb-1">{item.name}</h6>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <label className="small text-muted mb-0">Qty:</label>
                            <div className="input-group" style={{ width: '100px' }}>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                style={{ padding: '0.1rem 0.4rem' }}
                              >
                                <i className="bi bi-dash"></i>
                              </button>
                              <input
                                type="number"
                                className="form-control form-control-sm text-center"
                                value={item.quantity}
                                onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                                min="1"
                                style={{ padding: '0.1rem' }}
                              />
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                style={{ padding: '0.1rem 0.4rem' }}
                              >
                                <i className="bi bi-plus"></i>
                              </button>
                            </div>
                          </div>
                          <div className="text-end mt-1">
                            <strong style={{ color: ARCA_COLORS.primary }}>
                              {formatCurrency(item.price * item.quantity)}
                            </strong>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Order Totals */}
                  <div className="border-top pt-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Subtotal</span>
                      <strong>{formatCurrency(calculateSubtotal())}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Shipping</span>
                      <strong>
                        {calculateShipping() === 0 ? (
                          <span className="badge" style={{ backgroundColor: ARCA_COLORS.success }}>FREE</span>
                        ) : (
                          formatCurrency(calculateShipping())
                        )}
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between mb-3">
                      <span className="text-muted">Tax (8%)</span>
                      <strong>{formatCurrency(calculateTax())}</strong>
                    </div>
                    <div className="border-top pt-3 mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Total</h5>
                        <h4 className="mb-0 fw-bold" style={{ color: ARCA_COLORS.primary }}>
                          {formatCurrency(calculateTotal())}
                        </h4>
                      </div>
                    </div>

                    {/* Free Shipping Message */}
                    {calculateSubtotal() < 100 && (
                      <div className="alert alert-warning border-0 mb-3" style={{ backgroundColor: '#fff3cd' }}>
                        <small>
                          <i className="bi bi-truck me-2"></i>
                          Add {formatCurrency(100 - calculateSubtotal())} more for FREE shipping!
                        </small>
                      </div>
                    )}

                    {/* Place Order Button */}
                    <button
                      onClick={placeOrder}
                      disabled={loading}
                      className="btn btn-lg w-100 text-white fw-bold"
                      style={{
                        backgroundColor: ARCA_COLORS.primary,
                        borderColor: ARCA_COLORS.primary
                      }}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-lock-fill me-2"></i>
                          Place Order - {formatCurrency(calculateTotal())}
                        </>
                      )}
                    </button>

                    {/* Security Badges */}
                    <div className="mt-3 text-center">
                      <small className="text-muted">
                        <i className="bi bi-shield-check me-1" style={{ color: ARCA_COLORS.success }}></i>
                        Secure SSL encrypted transaction
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

// Wrapper component to provide Stripe Elements context
const Checkout = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm {...props} />
    </Elements>
  );
};

export default Checkout;